import React, { createContext, useContext } from 'react'
import type { AuthorizationHookResult } from '../hooks/authorization/useAuthorization'

const AuthorizationContext = createContext<AuthorizationHookResult | undefined>(undefined)

interface AuthorizationProviderProps {
  children: React.ReactNode
  /**
   * Precomputed authorization state (see hooks/authorization/useAuthorization). App.tsx calls the
   * hook itself -- rather than this provider calling it internally -- because it must know
   * whether the user has any allowed role *before* deciding whether to render the app shell at
   * all (see the "Not authorized" gate in App.tsx).
   */
  value: AuthorizationHookResult
}

export const AuthorizationProvider: React.FC<AuthorizationProviderProps> = ({ children, value }) => (
  <AuthorizationContext.Provider value={value}>{children}</AuthorizationContext.Provider>
)

/**
 * Exposes the current user's roles app-wide. Use `useHasRole` for gating UI -- this is a
 * convenience for reading the full authorization state (loading/error/roles) directly.
 */
export const useAuthorizationContext = (): AuthorizationHookResult => {
  const ctx = useContext(AuthorizationContext)
  if (ctx === undefined) {
    throw new Error('useAuthorizationContext must be used inside AuthorizationProvider')
  }
  return ctx
}

/**
 * Checks whether the current user has a role with the given friendly name. UX-only gating --
 * see components/authorization/RequireRole.tsx for why this must never be treated as the
 * security boundary; the backend authorizes every operation independently.
 */
export const useHasRole = (friendlyName: string): boolean => useAuthorizationContext().hasRole(friendlyName)

/**
 * True only for the CDHX-DevOps role (CDHX-ReadOnly/CDHX-Support are read-only for now). UX-only
 * gating -- see components/authorization/RequireWriteAccess.tsx.
 */
export const useHasWriteAccess = (): boolean => useAuthorizationContext().hasWriteAccess
